import React from 'react';
import { Form, Input, InputNumber, Typography } from 'antd';
// Types
import { CampaignSmPlatform } from 'api/graphql-global-types';
// Helpers
import { TimeLineDeliverable } from 'helpers/deliverables';
// Components
import TalentQuestions from 'pages/CampaignDetails/CampaignDetails/components/TalentQuestions/TalentQuestions';
import CompanyDetails from 'components/common/CampaignEditComponents/CompanyDetails/CompanyDetails';
import CustomDeliverables from '../CustomDeliverables/CustomDeliverables';
import UsageRightsFields from '../UsageRightsFields/UsageRightsFields';
// Style
import styles from './BriefSection.module.scss';

const { Text } = Typography;
const { TextArea } = Input;

type BriefSectionProps = {
  deliverablesChecklist: TimeLineDeliverable[];
  setDeliverablesChecklist: React.Dispatch<
    React.SetStateAction<TimeLineDeliverable[]>
  >;
  availablePlatforms: CampaignSmPlatform[];
  deliverablesValidationMessage: string | null;
  noUsageRights: boolean;
  onUsageRightsChange: () => void;
  showApplicationWindow?: boolean;
  showQuestions?: boolean;
};

const BriefSection = ({
  deliverablesChecklist,
  setDeliverablesChecklist,
  availablePlatforms,
  deliverablesValidationMessage,
  noUsageRights,
  onUsageRightsChange,
  showApplicationWindow = true,
  showQuestions = true,
}: BriefSectionProps): JSX.Element => {
  return (
    <section className={styles.section}>
      <h3 className={styles.sectionTitle}>Brief</h3>

      <div className={styles.subSection}>
        <div className={styles.row}>
          <Form.Item
            name="name"
            rules={[{ required: true, message: 'This field is required' }]}
            label={<Text>External Campaign Name</Text>}
          >
            <Input
              type="name"
              placeholder="Enter External Campaign Name"
              aria-label="External Campaign Name"
            />
          </Form.Item>
          <Form.Item
            name="internalName"
            label={<Text>Internal Campaign Name</Text>}
          >
            <Input
              placeholder="Enter Internal Campaign Name"
              aria-label="Internal Campaign Name"
            />
          </Form.Item>
        </div>

        {showApplicationWindow && (
          <Form.Item
            name="applicationDeadlineDays"
            label={<Text>Application Window</Text>}
            rules={[{ required: true, message: 'This field is required' }]}
            style={{ maxWidth: 320 }}
          >
            <InputNumber
              min={1}
              max={999}
              step={1}
              precision={0}
              controls={false}
              parser={(value) => {
                const digits = (value ?? '').replace(/\D/g, '');
                if (!digits) return '' as unknown as number;
                return Math.min(999, Number(digits));
              }}
              style={{ width: '100%' }}
              placeholder="Enter application window in days"
              aria-label="Application Window"
              addonAfter="days"
            />
          </Form.Item>
        )}
      </div>

      <div className={styles.subSection}>
        <CompanyDetails
          renderCustomFields={() => (
            <Form.Item name="instructions" label={<Text>Campaign Brief</Text>}>
              <TextArea
                autoSize={{ minRows: 4 }}
                placeholder="Describe the campaign goals, messaging, and what talent should know before applying."
              />
            </Form.Item>
          )}
        />
      </div>

      <div className={styles.subSection}>
        <h4 className={styles.subSectionTitle}>Deliverables</h4>
        <CustomDeliverables
          deliverablesChecklist={deliverablesChecklist}
          setDeliverablesChecklist={setDeliverablesChecklist}
          availablePlatforms={availablePlatforms}
          error={deliverablesValidationMessage}
        />
      </div>

      <div className={styles.subSection}>
        <UsageRightsFields
          noUsageRights={noUsageRights}
          onUsageRightsChange={onUsageRightsChange}
        />
      </div>

      <div className={styles.subSection}>
        <h4 className={styles.subSectionTitle}>Talent Guidance</h4>
        <Text type="secondary" className={styles.subSectionDescription}>
          Share clear do&apos;s and don&apos;ts to help talent understand your
          expectations.
        </Text>
        <div className={styles.row}>
          <Form.Item name="encouraged" label={<Text>Do&apos;s</Text>}>
            <TextArea
              autoSize={{ minRows: 4 }}
              placeholder="What you want talent to include, highlight, or emphasize."
            />
          </Form.Item>

          <Form.Item name="avoid" label={<Text>Don&apos;ts</Text>}>
            <TextArea
              autoSize={{ minRows: 4 }}
              placeholder="Topics, phrases, or actions talent should avoid."
            />
          </Form.Item>
        </div>
      </div>

      {showQuestions && (
        <div className={styles.subSection}>
          <h4 className={styles.subSectionTitle}>Application Questions</h4>
          <TalentQuestions
            name="questions"
            placeholder="Type a question..."
            editable
            maxQuestions={5}
          />
        </div>
      )}
    </section>
  );
};

export default BriefSection;
